import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Image,
  Text,
  useDisclosure,
  useMediaQuery,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { FunctionComponent, useEffect, useRef, useState } from 'react'
import AuthorityDetailsPanel from './AuthorityDetailsPanel'
import ProductDetailsPanel from './ProductDetailsPanel'
import ProductDetailsCustom from './ProductDetailsPanelCustom'
import PrescribedItemList from './PrescribedItemList'
import PatientDetailsPanel from './PatientDetailsPanel'
import PrescriberDetailsPanel from './PrescriberDetailsPanel'
import SendScriptPanel from './SendScriptPanel/SendScriptPanel'
import { ScrollableContainer } from '../../components/shared-components/ScrollableContainer'
import ResetScriptModal from './ResetScriptModal'
import { FeatureAnnouncementModal } from './FeatureAnnouncement'
import { PrescribedItem, Prescriber } from '../../types'
import { useAppDispatch, useAppSelector } from '../../app/typedReduxHooks'
import {
  detailsToPrescribedItem,
  selectedPrescribedItemSelector,
} from './ScriptForm/PrescribedItem'
import {
  addPrescribedItem,
  deletePrescribedItem,
  setAddPrescribedItemError,
  updatePrescribedItem,
} from './ScriptForm/PrescribedItem/PrescribedItemSlice'
import {
  PbsConcise,
  PbsRestriction,
  resetProductResults,
  selectProduct,
  setSearchTerm,
} from './ScriptForm/ProductSearchContextSlice'
import { transformPbsData } from './ScriptForm/PrescribedItem/pbsDataTransformer'
import UnauthorizedPage from '../../components/UnauthorizedPage'
import MimsIntegrated from '../../assets/mims-integrated.png'
import { preCheckProduct } from './ScriptForm/PrescribedItem/utils/preCheckProduct'
import { isPbsAuthorityNumberMissing } from './utilities'
import { PrescribedItemErrors } from '../../types/prescription'
import {
  initFormError,
  REQUIRE_AUTHORITY_MESSAGE,
  QUANTITY_MUST_BE_GREATER_THAN_ZERO_MESSAGE,
} from './constants'
import { RootState } from '../../app/store'
import ResponsiveStack from '../../components/shared-components/ResponsiveStack'
import { SubscriptionNotificationModal } from '../Billing/SubscriptionNotificationModal'
import { useSubscriptionGuard } from '../../hooks/useSubscriptionGuard'
import { validatePrescriberProfile } from '../PrescriberProfilePage/utils'
import { PrescriberProfileUnCompletedModal } from '../PrescriberProfilePage/components/PrescriberProfileUnCompletedModal'
import { Permission } from '../../types/permission'
import TrialExpirationBanner from '../../components/TrialExpirationBanner'
import { LowRemainingTokensNotificationContent } from '../Billing/components/LowRemainingTokensNotificationContent'
import { FundingSource } from '../../types/organization'
import { DESKTOP_BREAKPOINT } from '../../constants'

const NewScriptPage: FunctionComponent<unknown> = () => {
  const prescribedItemListRef = useRef<HTMLDivElement | null>(null)
  const dispatch = useAppDispatch()
  const { isOrgRegistrationAndPaymentFeatureOn, isSubscriptionInvalid } = useSubscriptionGuard()
  const permissions = useAppSelector((state) => state.authentication.permissions)
  const prescriber: Prescriber | null = useAppSelector(
    ({ prescriber }: RootState) => prescriber?.prescriber
  )
  const { prescribedItemList, selectedPrescribedItemIndex } = useAppSelector(
    (state) => state.newScript
  )
  const organizationSettings = useAppSelector((state) => state.organization?.data?.settings)
  const isAbleToSelectS8Medicine = organizationSettings?.enableS8Medicine
  const requireReasonForPrescription = organizationSettings?.requireReasonForPrescription
  const [isPrescriberProfileUnCompleted, setIsPrescriberProfileUnCompleted] = useState(false)
  const defaultOrganizationFoundingSource = organizationSettings?.defaultFundingSource

  const [disabledSubmit, setDisabledSubmit] = useState(true)
  const [formErrors, setFormErrors] = useState<Record<string, PrescribedItemErrors>>({})
  const selectedPrescribedItem = useAppSelector(selectedPrescribedItemSelector)
  const { selectedProduct, productSearchTerm } = useAppSelector((state) => state.product)
  const selectedPatient = useAppSelector(
    ({ patientDetail }: RootState) => patientDetail.selectedPatient
  )
  const validPrescribedItems = prescribedItemList.filter((item) => !!item)

  const isOnlyPrivateMedicationAllowed = selectedPatient
    ? selectedPatient.ihi_number && !selectedPatient.medicare_no && !selectedPatient.dva_file_no
    : false
  const toast = useToast()

  const details =
    productSearchTerm.length >= 3 ? selectedProduct : selectedPrescribedItem?.productDetails

  useEffect(() => {
    if (selectedPatient?.family_name && validPrescribedItems.length > 0) {
      const isAllPrescribedItemsValid = validPrescribedItems.every(
        (item) => item != null && Number(item.quantity) > 0 && !isPbsAuthorityNumberMissing(item)
      )
      setFormErrors((currentFormErrors) => {
        let hasChanged = false
        const nextFormErrors = { ...currentFormErrors }

        validPrescribedItems.forEach((item) => {
          if (!item) return

          const needsPbsAuthority = isPbsAuthorityNumberMissing(item)
          const pbsAuthorityErrorBase = nextFormErrors[item.productId]?.pbsAuthority ??
            initFormError.pbsAuthority ?? {
              message: REQUIRE_AUTHORITY_MESSAGE,
              showError: false,
            }

          if (pbsAuthorityErrorBase.showError === needsPbsAuthority) {
            return
          }

          nextFormErrors[item.productId] = {
            ...nextFormErrors[item.productId],
            pbsAuthority: {
              ...pbsAuthorityErrorBase,
              showError: needsPbsAuthority,
            },
          }
          hasChanged = true
        })

        return hasChanged ? nextFormErrors : currentFormErrors
      })
      setDisabledSubmit(!isAllPrescribedItemsValid)
      return
    }
    setDisabledSubmit(true)
  }, [validPrescribedItems, selectedPatient])

  useEffect(() => {
    if (prescriber) {
      setIsPrescriberProfileUnCompleted(!validatePrescriberProfile(prescriber))
    }
  }, [prescriber, validatePrescriberProfile])

  // Handle Drawer
  const { isOpen, onClose, onToggle } = useDisclosure()

  const isFormValid = () => {
    if (!validPrescribedItems.length) return false

    let isValid = true

    const errors = structuredClone(formErrors)
    Object.values(prescribedItemList).forEach((item) => {
      if (!item) return
      const { patientInstructions, reasonForPrescribing, productId, quantity } = item
      if (!patientInstructions) {
        errors[productId] = {
          ...errors[productId],
          patientInstructions: {
            ...(errors[productId]?.patientInstructions || {}),
            showError: !patientInstructions,
          },
        }
        isValid = false
      }
      if (requireReasonForPrescription && !reasonForPrescribing) {
        errors[productId] = {
          ...errors[productId],
          reasonForPrescribing: {
            ...(errors[productId]?.reasonForPrescribing || {}),
            showError: !reasonForPrescribing,
          },
        }
        isValid = false
      }
      const quantityNum = Number(quantity)
      const quantityInvalid = !Number.isFinite(quantityNum) || quantityNum <= 0
      const quantityErrorBase = errors[productId]?.quantity ??
        initFormError.quantity ?? {
          message: QUANTITY_MUST_BE_GREATER_THAN_ZERO_MESSAGE,
          showError: false,
        }
      errors[productId] = {
        ...errors[productId],
        quantity: {
          ...quantityErrorBase,
          showError: quantityInvalid,
        },
      }
      if (quantityInvalid) {
        isValid = false
      }
      const pbsAuthorityErrorBase = errors[productId]?.pbsAuthority ??
        initFormError.pbsAuthority ?? {
          message: REQUIRE_AUTHORITY_MESSAGE,
          showError: false,
        }
      const needsPbsAuthority = isPbsAuthorityNumberMissing(item)
      errors[productId] = {
        ...errors[productId],
        pbsAuthority: {
          ...pbsAuthorityErrorBase,
          showError: needsPbsAuthority,
        },
      }
      if (needsPbsAuthority) {
        isValid = false
      }
    })

    setFormErrors(errors)
    return isValid
  }

  const handleSelect = (pbsData: PbsConcise, restriction?: PbsRestriction) => {
    if (!details) return

    const overrides = transformPbsData(pbsData, restriction)

    dispatch(setAddPrescribedItemError(undefined))

    // Handle Selected Case
    if (selectedPrescribedItem) {
      const pbsArray = selectedPrescribedItem?.productDetails?.pbs || []
      const selectedPBS = pbsArray.find(({ pbsCode }) => pbsCode === overrides.pbsCode)
      if (!selectedPBS) {
        return
      }

      onClose()
      return dispatch(
        updatePrescribedItem({
          index: selectedPrescribedItemIndex,
          updatedItem: {
            ...selectedPrescribedItem,
            ...overrides,
          },
        })
      )
    }

    // Handle Add Case
    const item = detailsToPrescribedItem(details, overrides, selectedPatient.ctg_flag)
    const { passed, message } = preCheckProduct(item.productDetails)
    if (message) {
      toast({
        title: passed ? 'Warning' : 'Error',
        description: message,
        status: passed ? 'warning' : 'error',
        duration: null,
        isClosable: true,
        position: 'top',
      })
    }
    if (!passed) {
      return
    }

    dispatch(setSearchTerm(''))
    dispatch(selectProduct(undefined))
    dispatch(resetProductResults())
    dispatch(setAddPrescribedItemError(''))
    onClose()

    return dispatch(updatePrescribedItem({ updatedItem: item, index: selectedPrescribedItemIndex }))
  }

  const [isDesktop] = useMediaQuery(`(min-width: ${DESKTOP_BREAKPOINT})`)

  return (
    <Flex height="100%" flexDirection="column" width={isDesktop ? 'calc(100vw - 284px)' : '100vw'}>
      {permissions?.includes(Permission.PRESCRIBING) ? (
        <>
          <TrialExpirationBanner />
          <LowRemainingTokensNotificationContent />
          <FeatureAnnouncementModal />
          <ScrollableContainer flexGrow="1" flexBasis="0" id={'new-script'}>
            {prescriber?.active && isPrescriberProfileUnCompleted && (
              <PrescriberProfileUnCompletedModal isOpen={isPrescriberProfileUnCompleted} />
            )}
            {isOrgRegistrationAndPaymentFeatureOn && <SubscriptionNotificationModal />}
            <VStack maxWidth="1200px" m="auto">
              <VStack p="12px" width="100%" alignItems="stretch" spacing="8">
                <ResponsiveStack
                  gap={[0.1, 3]}
                  justifyContent="space-between"
                  alignItems={['center', 'stretch']}
                >
                  <Text as="h1" fontSize="3xl" fontWeight="bold">
                    New Prescription
                  </Text>
                  <ResetScriptModal>
                    {({ openModal }) => (
                      <Button
                        onClick={openModal}
                        variant="outline"
                        colorScheme="orange"
                        alignSelf={['flex-end', 'stretch']}
                      >
                        Reset Script
                      </Button>
                    )}
                  </ResetScriptModal>
                </ResponsiveStack>
                <PrescriberDetailsPanel />
                <PatientDetailsPanel />
                <PrescribedItemList
                  ref={prescribedItemListRef}
                  prescribedItems={prescribedItemList}
                  toggleDrawer={onToggle}
                  onAdd={(item?: PrescribedItem) => {
                    dispatch(addPrescribedItem(item))
                    if (item && defaultOrganizationFoundingSource !== FundingSource.PRIVATE) {
                      onToggle()
                    }
                  }}
                  onUpdate={(item: PrescribedItem, index: number) => {
                    dispatch(
                      updatePrescribedItem({
                        updatedItem: item,
                        index,
                      })
                    )
                  }}
                  onDelete={(index: number) => dispatch(deletePrescribedItem(index))}
                  formErrors={formErrors}
                  setFormErrors={setFormErrors}
                  isAbleToSelectS8Medicine={!!isAbleToSelectS8Medicine}
                  isOnlyPrivateMedicationAllowed={!!isOnlyPrivateMedicationAllowed}
                />
                <SendScriptPanel
                  disabledSubmit={disabledSubmit || isSubscriptionInvalid}
                  isFormValid={isFormValid}
                  isActive={!!prescriber?.active}
                  prescribedItemListRef={prescribedItemListRef}
                />
              </VStack>
            </VStack>

            <Drawer
              isOpen={isOpen}
              placement="right"
              onClose={onClose}
              size="md"
              blockScrollOnMount={false}
            >
              <DrawerOverlay />
              <DrawerContent>
                <DrawerCloseButton />
                <DrawerHeader>
                  <HStack justifyContent="start" alignItems="start">
                    <Text>Product Details</Text>
                    <Image src={MimsIntegrated} alt="Mims Integrated" width="100px" />
                  </HStack>
                </DrawerHeader>
                <DrawerBody>
                  <VStack align="stretch" gap="16px">
                    {details ? (
                      <>
                        <ProductDetailsPanel details={details} />
                        <AuthorityDetailsPanel
                          details={details}
                          handleSelect={handleSelect}
                          isOnlyPrivateMedicationAllowed={!!isOnlyPrivateMedicationAllowed}
                        />
                      </>
                    ) : (
                      <ProductDetailsCustom details={selectedPrescribedItem} />
                    )}
                  </VStack>
                </DrawerBody>
              </DrawerContent>
            </Drawer>
          </ScrollableContainer>
        </>
      ) : (
        <UnauthorizedPage />
      )}
    </Flex>
  )
}

export default NewScriptPage
